import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Upload, Loader2, CheckCircle2, FileText, Download, Trash2, ShieldAlert,
  Database, Users, Calendar, User as UserIcon, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

const fmtNumber = (n) => (n ?? 0).toLocaleString('en-IN');

const RegistryCard = ({
  source, summary, accent, onDownload, onDelete, downloading, deleting,
}) => {
  const hasData = summary && summary.totalRecords > 0;
  return (
    <Card
      className={`overflow-hidden border-2 transition-all hover:shadow-md ${accent.border}`}
      data-testid={`registry-card-${source}`}
    >
      <div className={`h-1.5 ${accent.bg}`} />
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${accent.tint}`}>
              <Database className={`h-6 w-6 ${accent.text}`} />
            </div>
            <div>
              <CardTitle className="text-xl">
                {source} Historical Database
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Duplicate registry · used silently when new leads arrive
              </CardDescription>
            </div>
          </div>
          {hasData
            ? <Badge className={`${accent.tint} ${accent.text} border-0`}>Active</Badge>
            : <Badge variant="outline" className="text-muted-foreground">Empty</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
              <Users className="h-3 w-3" /> Records
            </div>
            <p className="text-2xl font-bold tabular-nums">
              {fmtNumber(summary?.totalRecords)}
            </p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
              <Calendar className="h-3 w-3" /> Last Upload
            </div>
            <p className="text-sm font-medium">
              {summary?.lastUploadedAt
                ? fmtDateTime(summary.lastUploadedAt).split(',')[0]
                : 'Never'}
            </p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
              <UserIcon className="h-3 w-3" /> Uploaded By
            </div>
            <p className="text-sm font-medium truncate">
              {summary?.lastUploadedByName || '—'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!hasData || downloading}
            onClick={onDownload}
            className="flex-1 sm:flex-none"
            data-testid={`download-${source}`}
          >
            {downloading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />}
            Download CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasData || deleting}
            onClick={onDelete}
            className="flex-1 sm:flex-none text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
            data-testid={`delete-${source}`}
          >
            {deleting
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Trash2 className="h-4 w-4 mr-2" />}
            Delete Database
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ACCENT = {
  Servall: {
    bg: 'bg-emerald-500', tint: 'bg-emerald-50',
    text: 'text-emerald-700', border: 'border-emerald-100',
  },
  R2G: {
    bg: 'bg-sky-500', tint: 'bg-sky-50',
    text: 'text-sky-700', border: 'border-sky-100',
  },
};

const ImportPage = () => {
  const { api } = useAuth();
  const [summary, setSummary] = useState({ Servall: null, R2G: null });
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [file, setFile] = useState(null);
  const [source, setSource] = useState('Servall');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Upload confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPreview, setPendingPreview] = useState(null); // {file, source, estimatedRows}

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [downloading, setDownloading] = useState(null); // source while downloading

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/lead-database/summary');
      setSummary(data);
    } catch (e) {
      toast.error('Failed to load registry summary');
    } finally {
      setLoadingSummary(false);
    }
  }, [api]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // ---------- Upload (with confirmation) ----------
  const initiateUpload = () => {
    if (!file) return toast.error('Please choose a CSV file first');
    if (!source) return toast.error('Pick Servall or R2G as the source');
    // Estimate row count by counting newlines (rough — we don't parse yet)
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result || '');
      const lineCount = text.split(/\r?\n/).filter(Boolean).length;
      const estimatedRows = Math.max(0, lineCount - 1); // minus header
      setPendingPreview({ file, source, estimatedRows });
      setConfirmOpen(true);
    };
    reader.onerror = () => {
      // fallback — just open with unknown count
      setPendingPreview({ file, source, estimatedRows: null });
      setConfirmOpen(true);
    };
    reader.readAsText(file);
  };

  const handleConfirmedUpload = async () => {
    if (!pendingPreview) return;
    setConfirmOpen(false);
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', pendingPreview.file);
      fd.append('source', pendingPreview.source);
      const { data } = await api.post('/lead-database/bulk-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(data);
      toast.success(`${fmtNumber(data.uploaded)} rows inserted into ${data.source} database`);
      await loadSummary();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setUploading(false);
      setPendingPreview(null);
    }
  };

  // ---------- Download ----------
  const handleDownload = async (src) => {
    setDownloading(src);
    try {
      const response = await api.get(`/lead-database/download/${src}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dt = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `${src}_historical_database_${dt}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${src} CSV exported`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Download failed';
      toast.error(typeof msg === 'string' ? msg : 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  // ---------- Delete ----------
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/lead-database/source/${deleteTarget}`);
      toast.success(data.message || `${deleteTarget} registry cleared`);
      await loadSummary();
      setDeleteTarget(null);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Delete failed';
      toast.error(typeof msg === 'string' ? msg : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Database className="h-7 w-7 text-emerald-600" /> Historical Database
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">
            Upload, manage, and export the Servall &amp; R2G duplicate registries.
            These records are used silently when new leads arrive via the Google
            Sheets Engine — they never appear in LMS or the Lead Board.
          </p>
        </div>
        <Button variant="outline" onClick={loadSummary} disabled={loadingSummary}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loadingSummary ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RegistryCard
          source="Servall"
          summary={summary.Servall}
          accent={ACCENT.Servall}
          onDownload={() => handleDownload('Servall')}
          onDelete={() => setDeleteTarget('Servall')}
          downloading={downloading === 'Servall'}
          deleting={deleting && deleteTarget === 'Servall'}
        />
        <RegistryCard
          source="R2G"
          summary={summary.R2G}
          accent={ACCENT.R2G}
          onDownload={() => handleDownload('R2G')}
          onDelete={() => setDeleteTarget('R2G')}
          downloading={downloading === 'R2G'}
          deleting={deleting && deleteTarget === 'R2G'}
        />
      </div>

      {/* UPLOAD CARD */}
      <Card data-testid="upload-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Upload className="h-5 w-5" /> Upload New Database
          </CardTitle>
          <CardDescription>
            CSV will be inserted as-is — no duplicate filtering at upload time.
            Mobile numbers are auto-normalized to last 10 digits.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertTitle>CSV format</AlertTitle>
            <AlertDescription className="text-xs sm:text-sm">
              Required column: <code className="px-1 py-0.5 bg-muted rounded">Mobile</code> (or Phone / Contact / WhatsApp).
              Optional: <code className="px-1 py-0.5 bg-muted rounded">Name</code>,
              <code className="px-1 py-0.5 bg-muted rounded ml-1">City</code>,
              <code className="px-1 py-0.5 bg-muted rounded ml-1">Platform</code>.
              Header row required.
            </AlertDescription>
          </Alert>

          <div>
            <Label>Source *</Label>
            <RadioGroup
              value={source}
              onValueChange={setSource}
              className="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-2"
              data-testid="source-radio-group"
            >
              <div className="flex items-center space-x-2 border rounded-lg p-3 flex-1 cursor-pointer hover:bg-muted/40">
                <RadioGroupItem value="Servall" id="src-servall" data-testid="source-servall" />
                <Label htmlFor="src-servall" className="cursor-pointer flex-1">
                  <span className="font-semibold text-emerald-700">Servall</span>
                  <span className="block text-xs text-muted-foreground">Own historical data</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-3 flex-1 cursor-pointer hover:bg-muted/40">
                <RadioGroupItem value="R2G" id="src-r2g" data-testid="source-r2g" />
                <Label htmlFor="src-r2g" className="cursor-pointer flex-1">
                  <span className="font-semibold text-sky-700">R2G</span>
                  <span className="block text-xs text-muted-foreground">Partner database</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="csv-file">CSV File *</Label>
            <Input
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="cursor-pointer"
              data-testid="csv-file-input"
            />
            {file && (
              <p className="text-xs text-muted-foreground mt-1">
                Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          <Button
            onClick={initiateUpload}
            disabled={!file || uploading}
            className="w-full sm:w-auto"
            size="lg"
            data-testid="csv-upload-btn"
          >
            {uploading
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Upload className="h-4 w-4 mr-2" />}
            Upload to {source} Database
          </Button>

          {uploadResult && (
            <div className="border-2 border-emerald-200 bg-emerald-50/30 rounded-lg p-4 space-y-3"
              data-testid="csv-upload-result">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <p className="font-medium">Upload complete</p>
                <Badge variant="secondary">{uploadResult.source}</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="border rounded-lg p-3 bg-sky-50 border-sky-200">
                  <p className="text-xs uppercase tracking-wider text-sky-700">Rows Scanned</p>
                  <p className="text-2xl font-semibold mt-1 text-sky-800">{fmtNumber(uploadResult.totalRowsScanned)}</p>
                </div>
                <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
                  <p className="text-xs uppercase tracking-wider text-emerald-700">Inserted</p>
                  <p className="text-2xl font-semibold mt-1 text-emerald-800">{fmtNumber(uploadResult.uploaded)}</p>
                </div>
                <div className="border rounded-lg p-3 bg-rose-50 border-rose-200">
                  <p className="text-xs uppercase tracking-wider text-rose-700">Invalid Mobile</p>
                  <p className="text-2xl font-semibold mt-1 text-rose-800">{fmtNumber(uploadResult.skippedInvalidMobile)}</p>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Auto-detected columns:&nbsp;
                {Object.entries(uploadResult.detectedMapping || {})
                  .filter(([, v]) => !!v)
                  .map(([k, v]) => `${k} ← "${v}"`)
                  .join(' · ')}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* UPLOAD CONFIRMATION DIALOG */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="upload-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" /> Confirm Upload
            </DialogTitle>
            <DialogDescription>
              You are about to upload to the duplicate registry.
              These records will <strong>not</strong> appear in LMS.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="border rounded-lg divide-y">
              <div className="flex items-center justify-between p-3">
                <span className="text-sm text-muted-foreground">Source</span>
                <Badge className={`${ACCENT[pendingPreview?.source]?.tint} ${ACCENT[pendingPreview?.source]?.text} border-0`}>
                  {pendingPreview?.source}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3">
                <span className="text-sm text-muted-foreground">File</span>
                <span className="font-medium text-sm truncate ml-2">
                  {pendingPreview?.file?.name}
                </span>
              </div>
              <div className="flex items-center justify-between p-3">
                <span className="text-sm text-muted-foreground">Estimated rows</span>
                <span className="font-mono text-sm">
                  {pendingPreview?.estimatedRows != null
                    ? fmtNumber(pendingPreview.estimatedRows)
                    : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3">
                <span className="text-sm text-muted-foreground">File size</span>
                <span className="font-mono text-sm">
                  {pendingPreview?.file
                    ? (pendingPreview.file.size / 1024).toFixed(1) + ' KB'
                    : '—'}
                </span>
              </div>
            </div>
            <Alert>
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription className="text-xs">
                These records will only be used for <strong>duplicate checking</strong>.
                They will NOT appear in LMS / Lead Board.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleConfirmedUpload} disabled={uploading} data-testid="confirm-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Confirm & Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-5 w-5" /> Are you sure?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This will permanently remove the <strong>{deleteTarget} Historical Database</strong>
              {' '}from the Duplicate Registry.
              <br /><br />
              This action <strong>cannot be undone</strong>.
            </DialogDescription>
          </DialogHeader>
          <Alert className="border-emerald-200 bg-emerald-50/50">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertTitle className="text-emerald-800">Safe for your LMS data</AlertTitle>
            <AlertDescription className="text-xs text-emerald-700">
              Your LMS Leads, Lead Board, and CRM data will remain completely untouched.
              Only the {deleteTarget} duplicate-registry markers will be removed.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}
              data-testid="confirm-delete-btn">
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportPage;
